'use client';

import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowClockwiseIcon, CaretDownIcon, FunnelIcon, FunnelXIcon, LightbulbFilamentIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Menu, MenuItem } from '@tranquility-ai/theme-components/menu';
import { Modal } from '@tranquility-ai/theme-components/modal';
import '@tranquility-ai/theme-components/modal.css';
import { QuestionBar } from '@tranquility-ai/theme-components/question-bar';
import '@tranquility-ai/theme-components/question-bar.css';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { FeaturesContext, FullScreenContext, StatsContext } from '@/app/contexts';
import { useCasesList } from '@/hooks/useCasesList';
import { useDismissOnViewExit } from '@/hooks/useDismissOnViewExit';
import { usePersonalFilesCaseId } from '@/hooks/usePersonalFilesCaseId';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { isMobileAnalysisAvailableForCase } from '@/lib/mobile-analysis-agent';
import { recordStat } from '@/shared/functions';
import {
  buildQuestionSubmitScopes,
  caseFolderNameForChatScopeTag,
  mapScopeTagsToFolderNames,
} from './build-question-submit-scopes';
import { emptyScopesNew, useChatEvidenceLockerNew } from './chat-evidence-locker-context-new';
import { EvidenceLockerModalNew } from './evidence-locker-modal-new';
import {
  EvidenceFilterPickerNew,
  hasEvidenceFilterActive,
} from './evidence-filter-picker-new';
import {
  EvidenceSelectionChipsNew,
  hasQuestionBarScopeContent,
} from './evidence-selection-chips-new';
import { isFilterTooBig, type QuestionBarProps } from './question-bar';
import {
  QUESTION_BAR_ICON_BUTTON_BACKGROUND,
  QUESTION_BAR_ICON_BUTTON_CLASS,
} from './question-bar-ui';
import { isPromptFlagOn } from '@/i18n/promptCatalog';
import { PromptLibrary } from './prompt-library';

const ASSISTANT_MODE = 'assistant';
const DEEP_DIVE_MODE = 'deepDive';
const MOBILE_MODE = 'mobile';

/**
 * TimePilot chat question bar on the theme `QuestionBar`.
 * Analysis modes sit beside send. The menu is anchored to that button, and
 * its caret flips while open. After a conversation starts, the docked bar
 * stays at its resting width until the field is focused, then eases wider.
 * That resting width is also the minimum. Scope chips and an open analysis
 * mode menu keep the docked bar expanded and opaque. The empty-state bar
 * stays full size.
 */
export function QuestionBarMigrated({
  caseId,
  onSubmit,
  userId,
  sessionId,
  onClearThread,
  isLoading = false,
  isResetting,
  setQuery,
  query,
  textareaRef,
  hasChatHistory,
  holdSubmitVisualLock = false,
  collapseSources,
  scopesAndFiles,
  setScopesAndFiles,
  setShowFolders,
  addToTimeline,
  featureFlagsProp,
  triggerPromptLibrary,
  onAssistantEvidenceFilterCleared,
  assistantViewSelected = true,
  assistantViewVisible = true,
}: QuestionBarProps) {
  const t = useTimePilotT();
  const featuresContext = useContext(FeaturesContext);
  const statsContext = useContext(StatsContext);
  const { isFullscreen } = useContext(FullScreenContext);
  const featureFlags = featureFlagsProp ?? featuresContext?.featureFlags ?? {};
  const personalFilesCaseId = usePersonalFilesCaseId();
  const casesQuery = useCasesList();
  const { registerLockerOpener } = useChatEvidenceLockerNew();

  const barRef = useRef<HTMLDivElement>(null);
  const promptLibraryRef = useRef<HTMLDivElement>(null);
  const pathMenuCount = useRef(0);
  const [mode, setMode] = useState(ASSISTANT_MODE);
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  const [isEvidenceLockerOpen, setIsEvidenceLockerOpen] = useState(false);
  const [filterPickerOpen, setFilterPickerOpen] = useState(false);
  const [isPromptLibraryOpen, setIsPromptLibraryOpen] = useState(false);
  const [suppressPromptLibraryTooltip, setSuppressPromptLibraryTooltip] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(!!isResetting);
  const [pathMenuOpen, setPathMenuOpen] = useState(false);

  const handlePathMenuOpenChange = useCallback((isOpen: boolean) => {
    pathMenuCount.current = Math.max(0, pathMenuCount.current + (isOpen ? 1 : -1));
    setPathMenuOpen(pathMenuCount.current > 0);
  }, []);

  const contentCategoriesEnabled = !!featureFlags?.contentCategories;
  const mobileAvailable = isMobileAnalysisAvailableForCase(
    featureFlags,
    caseId,
    personalFilesCaseId,
  );
  const evidenceSelected = hasEvidenceFilterActive(scopesAndFiles);
  const showScope = hasQuestionBarScopeContent(scopesAndFiles, contentCategoriesEnabled);
  const submitUiLocked = holdSubmitVisualLock || isLoading;
  const filterLimit = featureFlags?.filterSizeLimit as unknown as number | undefined;
  const filterIsTooBig = !!(
    filterLimit && isFilterTooBig(filterLimit, scopesAndFiles, caseId)
  );

  const showPromptLibrary = isPromptFlagOn(featureFlags?.promptLibrary);

  const modeOptions = [
    { value: ASSISTANT_MODE, label: t('chat.composer.modeAssistant') },
    { value: DEEP_DIVE_MODE, label: t('chat.composer.deepDive') },
    ...(mobileAvailable
      ? [{ value: MOBILE_MODE, label: t('chat.composer.mobile') }]
      : []),
  ];

  useEffect(() => {
    if (mode === MOBILE_MODE && !mobileAvailable) {
      setMode(ASSISTANT_MODE);
    }
  }, [mode, mobileAvailable]);

  useEffect(() => {
    registerLockerOpener(() => setIsEvidenceLockerOpen(true));
    return () => registerLockerOpener(null);
  }, [registerLockerOpener]);

  useLayoutEffect(() => {
    const textarea = barRef.current?.querySelector('textarea') ?? null;
    (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = textarea;
  });

  useEffect(() => {
    // Fullscreen media sets assistantViewVisible to false while the chat remains
    // mounted. Keep the portaled locker open so minimizing restores its preview.
    if (assistantViewVisible || isFullscreen) return;
    setIsEvidenceLockerOpen(false);
    setFilterPickerOpen(false);
    setModeMenuOpen(false);
  }, [assistantViewVisible, isFullscreen]);

  useDismissOnViewExit(assistantViewSelected, () => {
    setIsEvidenceLockerOpen(false);
    setFilterPickerOpen(false);
    setModeMenuOpen(false);
  });

  useEffect(() => {
    if (!isResetting) {
      setIsAlertOpen(false);
    }
  }, [isResetting]);

  useEffect(() => {
    if (triggerPromptLibrary) {
      setIsPromptLibraryOpen(true);
      setSuppressPromptLibraryTooltip(false);
    }
  }, [triggerPromptLibrary]);

  const mapScopeTags = useCallback(
    (tags: string[]) =>
      mapScopeTagsToFolderNames(tags, (tag) =>
        caseFolderNameForChatScopeTag(tag, casesQuery.data),
      ),
    [casesQuery.data],
  );

  const handleSubmit = useCallback(
    async (value: string) => {
      if (filterIsTooBig || submitUiLocked) return;
      const trimmed = value.trim();
      if (!trimmed) return;
      setQuery(trimmed);
      try {
        await onSubmit(
          trimmed,
          buildQuestionSubmitScopes({
            scopesAndFiles,
            contentCategoriesEnabled,
            mapScopeTags,
          }),
          userId,
          sessionId,
          mode === DEEP_DIVE_MODE,
          mode === MOBILE_MODE,
        );
        collapseSources();
      } catch (error) {
        console.error('Error submitting query:', error);
      }
    },
    [
      collapseSources,
      contentCategoriesEnabled,
      filterIsTooBig,
      mapScopeTags,
      mode,
      onSubmit,
      scopesAndFiles,
      sessionId,
      setQuery,
      submitUiLocked,
      userId,
    ],
  );

  const clearAllEvidenceFilters = useCallback(() => {
    setScopesAndFiles(emptyScopesNew());
    setShowFolders(false);
    onAssistantEvidenceFilterCleared?.();
    setFilterPickerOpen(false);
    setIsEvidenceLockerOpen(false);
  }, [
    onAssistantEvidenceFilterCleared,
    setScopesAndFiles,
    setShowFolders,
  ]);

  const handleFunnelClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (isEvidenceLockerOpen) {
        setIsEvidenceLockerOpen(false);
        setFilterPickerOpen(true);
        return;
      }
      setFilterPickerOpen((open) => !open);
    },
    [isEvidenceLockerOpen],
  );

  const handlePromptLibraryOpenChange = useCallback((open: boolean) => {
    if (open) {
      setSuppressPromptLibraryTooltip(false);
    } else {
      setSuppressPromptLibraryTooltip(true);
      requestAnimationFrame(() => {
        document.getElementById('prompt-library-open-button')?.blur();
      });
    }
    setIsPromptLibraryOpen(open);
  }, []);

  const placeholder = evidenceSelected
    ? t('chat.composer.placeholderScoped')
    : t('chat.composer.placeholder');

  // The portaled mode menu drops focus, so an open menu keeps the bar expanded.
  const keepExpanded = pathMenuOpen || showScope || modeMenuOpen;
  const shellClassName = [
    'box-border z-[2] m-0 flex flex-col items-center justify-center gap-1 p-0 outline-none',
    hasChatHistory
      ? `fixed left-1/2 -translate-x-1/2 min-w-[64.17%] transition-[width] duration-[180ms] ease motion-reduce:transition-none sm:min-w-[35%] ${
          keepExpanded
            ? 'w-11/12 sm:w-6/12'
            : 'w-[64.17%] focus-within:w-11/12 sm:w-[35%] sm:focus-within:w-6/12'
        } ${
          featureFlags?.enableMediaAndSourceShare
            ? 'bottom-[10px] sm:bottom-[50px]'
            : 'bottom-0'
        }`
      : 'relative w-11/12 sm:w-6/12',
  ].join(' ');

  return (
    <>
      <div data-testid="question-bar-migrated" className={shellClassName}>
        <QuestionBar
          ref={barRef}
          className="w-full"
          value={query}
          onValueChange={setQuery}
          onSubmit={(value) => {
            void handleSubmit(value);
          }}
          placeholder={placeholder}
          loadingPlaceholder={t('chat.composer.loadingAnswers')}
          isLoading={submitUiLocked}
          aria-label={t('chat.composer.ariaLabel')}
          sendLabel={t(
            submitUiLocked
              ? 'chat.composer.submitting'
              : filterIsTooBig
                ? 'chat.composer.tooManyItems'
                : 'chat.composer.submit',
          )}
          dimWhenUnfocused={!!hasChatHistory && !keepExpanded}
          leftActions={
            <>
              <EvidenceFilterPickerNew
                isOpen={filterPickerOpen}
                onOpenChange={setFilterPickerOpen}
                onOpenEvidenceLocker={() => {
                  setFilterPickerOpen(false);
                  setIsEvidenceLockerOpen(true);
                }}
                onClearAllFilters={clearAllEvidenceFilters}
              >
                <TpTooltip
                  disabled={isEvidenceLockerOpen || filterPickerOpen}
                  content={
                    evidenceSelected
                      ? t('chat.evidence.filterActiveHelp')
                      : t('chat.evidence.filterHelp')
                  }
                  placement="left"
                  delayDuration={700}
                  sideOffset={4}
                >
                  <Button
                    type="button"
                    iconOnly
                    size="md"
                    color="light"
                    background="brand"
                    data-testid="open-evidence-locker"
                    aria-label={
                      isEvidenceLockerOpen
                        ? t('chat.evidence.closeLocker')
                        : filterPickerOpen
                          ? t('chat.evidence.closeFilterMenu')
                          : t('chat.evidence.openFilterMenu')
                    }
                    onClick={handleFunnelClick}
                  >
                    {evidenceSelected ? <FunnelXIcon /> : <FunnelIcon />}
                  </Button>
                </TpTooltip>
              </EvidenceFilterPickerNew>
              {showPromptLibrary ? (
                <div onPointerEnter={() => setSuppressPromptLibraryTooltip(false)}>
                  <TpTooltip
                    disabled={isPromptLibraryOpen || suppressPromptLibraryTooltip}
                    content={t('chat.composer.promptSuggestions')}
                    placement="left"
                    delayDuration={700}
                    sideOffset={4}
                  >
                    <div>
                      <PromptLibrary
                        featureFlags={featureFlagsProp}
                        onPromptSelect={(selectedQuery) => {
                          setQuery(selectedQuery);
                          barRef.current?.querySelector('textarea')?.focus();
                        }}
                        onOpenChange={handlePromptLibraryOpenChange}
                        isOpen={isPromptLibraryOpen}
                        popoverRef={promptLibraryRef}
                        triggerElement={
                          <Button
                            type="button"
                            iconOnly
                            size="md"
                            color="light"
                            background="brand"
                            id="prompt-library-open-button"
                            aria-label={t(
                              isPromptLibraryOpen
                                ? 'chat.composer.closePromptLibrary'
                                : 'chat.composer.openPromptLibrary',
                            )}
                            aria-pressed={isPromptLibraryOpen}
                          >
                            <LightbulbFilamentIcon />
                          </Button>
                        }
                      />
                    </div>
                  </TpTooltip>
                </div>
              ) : null}
              {hasChatHistory ? (
                <Button
                  type="button"
                  size="md"
                  style="text"
                  color="light"
                  background={QUESTION_BAR_ICON_BUTTON_BACKGROUND}
                  className={`${QUESTION_BAR_ICON_BUTTON_CLASS} whitespace-nowrap`}
                  leadingIcon={<ArrowClockwiseIcon />}
                  onClick={() => setIsAlertOpen(true)}
                >
                  {t('chat.reset.newSession')}
                </Button>
              ) : null}
            </>
          }
          rightActions={
            <Menu
              isOpen={modeMenuOpen}
              onOpenChange={setModeMenuOpen}
              placement="top-end"
              aria-label={t('chat.composer.modeSelectorLabel')}
              content={
                <>
                  {modeOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      checked={option.value === mode}
                      onSelect={() => setMode(option.value)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </>
              }
            >
              <Button
                type="button"
                style="text"
                color="light"
                background={QUESTION_BAR_ICON_BUTTON_BACKGROUND}
                size="md"
                aria-label={t('chat.composer.modeSelectorLabel')}
                className={`${QUESTION_BAR_ICON_BUTTON_CLASS} whitespace-nowrap`}
                trailingIcon={
                  <CaretDownIcon
                    weight="bold"
                    className={`transition-transform duration-150 ${modeMenuOpen ? 'rotate-180' : ''}`}
                  />
                }
                onClick={() => setModeMenuOpen((open) => !open)}
              >
                {modeOptions.find((option) => option.value === mode)?.label ??
                  t('chat.composer.modeAssistant')}
              </Button>
            </Menu>
          }
          scope={
            showScope ? (
              <EvidenceSelectionChipsNew
                layout="embedded"
                onPathMenuOpenChange={handlePathMenuOpenChange}
              />
            ) : undefined
          }
        />
        {filterIsTooBig ? (
          <p role="alert" className="m-0 max-w-full text-sm text-white/90">
            {t('chat.composer.tooManyItemsHelp')}
          </p>
        ) : null}
      </div>
      <Modal
        id="start-new-session-confirm-modal"
        isOpen={isAlertOpen}
        onOpenChange={(open) => {
          if (!open) setIsAlertOpen(false);
        }}
        title={t('chat.reset.confirmTitle')}
        size="sm"
        showCloseButton={false}
        footer={
          <div className="flex w-full flex-row items-center justify-between gap-2">
            <Button
              id="CancelReset"
              title={t('chat.reset.cancel')}
              style="text"
              color="light"
              background="inverse"
              onClick={(event) => {
                event.stopPropagation();
                setIsAlertOpen(false);
                recordStat('CancelResetButtonClicked', event, statsContext);
              }}
            >
              {t('chat.reset.cancel')}
            </Button>
            <Button
              id="ResetConversationHistory"
              title={t('chat.reset.resetTitle')}
              style="solid"
              color="accent"
              background="inverse"
              disabled={!!isResetting}
              isLoading={!!isResetting}
              loadingText={t('chat.reset.starting')}
              onClick={(event) => {
                event.stopPropagation();
                onClearThread();
                recordStat('ResetConversationHistoryButtonClicked', event, statsContext);
              }}
            >
              {t('chat.reset.startNew')}
            </Button>
          </div>
        }
      >
        <p className="m-0 italic text-white/90">{t('chat.reset.confirmMessage')}</p>
      </Modal>
      <EvidenceLockerModalNew
        isOpen={isEvidenceLockerOpen}
        onClose={() => setIsEvidenceLockerOpen(false)}
        addToTimeline={addToTimeline}
      />
    </>
  );
}
